import { useState, useEffect } from 'react';
import { clsx } from 'clsx';
import { Card, Button, Input, Badge, PageHeader } from '../components/ui';
import { useSelectedBusinessId } from '../hooks/useSelectedBusiness';
import {
  Sparkles,
  Copy,
  RefreshCw,
  Edit2,
  Trash2,
  Loader2,
  AlertCircle,
  X,
  Check,
  Image,
  Tag,
  Save,
  Link2,
} from 'lucide-react';
import { API_URL, servicesApi, type Business, type Service } from '../lib/api';
import { getBusinessList } from '../lib/businessListCache';

const API_BASE = API_URL;

function resolveServiceImageUrl(imageUrl?: string): string | null {
  if (!imageUrl) return null;
  if (imageUrl.startsWith('http://') || imageUrl.startsWith('https://')) return imageUrl;

  const normalizedPath = imageUrl.startsWith('/') ? imageUrl : `/${imageUrl}`;
  const apiOrigin = API_BASE.startsWith('http') ? new URL(API_BASE).origin : window.location.origin;

  if (normalizedPath.startsWith('/images/')) {
    return `${API_BASE}${normalizedPath}`;
  }

  if (normalizedPath.startsWith('/api/')) {
    return `${apiOrigin}${normalizedPath}`;
  }

  return `${API_BASE}${normalizedPath}`;
}

function ServiceCard({ service, onReload }: { service: Service; onReload: () => void }) {
  const [isEditing, setIsEditing] = useState(false);
  const [isSaving, setIsSaving] = useState(false);
  const [isRegeneratingImage, setIsRegeneratingImage] = useState(false);
  const [isDeleting, setIsDeleting] = useState(false);
  const [isImageBroken, setIsImageBroken] = useState(false);
  const [copiedField, setCopiedField] = useState<string | null>(null);

  const [editData, setEditData] = useState({
    title: service.title,
    category: service.category || '',
    websiteUrl: service.websiteUrl || '',
    shortDesc: service.shortDesc,
    longDesc: service.longDesc,
    keywordsText: service.keywords.join(', '),
  });

  const handleCopy = (text: string, field: string) => {
    navigator.clipboard.writeText(text);
    setCopiedField(field);
    setTimeout(() => setCopiedField(null), 2000);
  };

  const handleSave = async () => {
    setIsSaving(true);
    try {
      await servicesApi.update(service.id, {
        title: editData.title.slice(0, 100),
        category: editData.category.slice(0, 100),
        websiteUrl: editData.websiteUrl.slice(0, 500),
        shortDesc: editData.shortDesc.slice(0, 250),
        longDesc: editData.longDesc.slice(0, 3000),
        keywords: editData.keywordsText.split(',').map((k) => k.trim()).filter(Boolean),
      });
      setIsEditing(false);
      onReload();
    } finally {
      setIsSaving(false);
    }
  };

  const handleDelete = async () => {
    if (!confirm('Удалить услугу?')) return;
    setIsDeleting(true);
    try {
      await servicesApi.delete(service.id);
      onReload();
    } finally {
      setIsDeleting(false);
    }
  };

  const handleRegenerateImage = async () => {
    setIsRegeneratingImage(true);
    try {
      await servicesApi.regenerateImage(service.id);
      onReload();
    } finally {
      setIsRegeneratingImage(false);
    }
  };

  useEffect(() => {
    setIsImageBroken(false);
  }, [service.imageUrl]);

  const imageUrl = resolveServiceImageUrl(service.imageUrl);
  const hasImage = !!imageUrl && !isImageBroken;
  const websiteUrl = service.websiteUrl
    ? (service.websiteUrl.startsWith('http://') || service.websiteUrl.startsWith('https://')
      ? service.websiteUrl
      : `https://${service.websiteUrl}`)
    : null;

  return (
    <Card padding="none" className="overflow-hidden">
      {/* Image */}
      <div className="relative aspect-square w-full bg-gray-100">
        {hasImage ? (
          <img
            src={imageUrl}
            alt={service.title}
            className="w-full h-full object-cover"
            onError={() => setIsImageBroken(true)}
          />
        ) : (
          <div className="w-full h-full flex flex-col items-center justify-center text-gray-300 gap-2">
            <Image className="w-12 h-12" />
            <span className="text-sm">Изображение не сгенерировано</span>
          </div>
        )}
        {imageUrl && (
          <button
            onClick={handleRegenerateImage}
            disabled={isRegeneratingImage}
            className="absolute top-2 right-2 p-1.5 bg-white/90 rounded-lg shadow text-gray-600 hover:text-gray-900 transition-colors"
            title="Обновить изображение"
          >
            {isRegeneratingImage ? <Loader2 className="w-4 h-4 animate-spin" /> : <RefreshCw className="w-4 h-4" />}
          </button>
        )}
      </div>

      <div className="p-4 space-y-3">
        {isEditing ? (
          /* Edit mode */
          <div className="space-y-4">
            <div>
              <Input
                label="Заголовок (до 100 символов)"
                value={editData.title}
                maxLength={100}
                onChange={(e) => setEditData((p) => ({ ...p, title: e.target.value }))}
              />
              <span className="text-xs text-gray-400">{editData.title.length}/100</span>
            </div>
            <Input
              label="Категория услуги"
              value={editData.category}
              maxLength={100}
              onChange={(e) => setEditData((p) => ({ ...p, category: e.target.value }))}
            />
            <Input
              label="Ссылка на сайт"
              value={editData.websiteUrl}
              maxLength={500}
              placeholder="https://example.ru"
              onChange={(e) => setEditData((p) => ({ ...p, websiteUrl: e.target.value }))}
            />
            <div>
              <Input
                label="Краткое описание (до 250 символов)"
                value={editData.shortDesc}
                maxLength={250}
                onChange={(e) => setEditData((p) => ({ ...p, shortDesc: e.target.value }))}
              />
              <span className="text-xs text-gray-400">{editData.shortDesc.length}/250</span>
            </div>
            <div>
              <label className="block text-sm font-medium text-gray-700 mb-1">Полное описание (до 3000 символов)</label>
              <textarea
                className="w-full px-3 py-2 border border-gray-300 rounded-lg text-sm text-gray-900 placeholder-gray-400 focus:outline-none focus:ring-2 focus:ring-primary-500 focus:border-primary-500"
                rows={6}
                value={editData.longDesc}
                maxLength={3000}
                onChange={(e) => setEditData((p) => ({ ...p, longDesc: e.target.value }))}
              />
              <span className="text-xs text-gray-400">{editData.longDesc.length}/3000</span>
            </div>
            <Input
              label="Ключевые слова (через запятую)"
              value={editData.keywordsText}
              onChange={(e) => setEditData((p) => ({ ...p, keywordsText: e.target.value }))}
            />
            <div className="flex gap-2">
              <Button size="sm" onClick={handleSave} loading={isSaving} icon={<Save className="w-4 h-4" />}>
                Сохранить
              </Button>
              <Button size="sm" variant="outline" onClick={() => setIsEditing(false)}>
                Отмена
              </Button>
            </div>
          </div>
        ) : (
          /* View mode */
          <>
            <div>
              <div className="flex items-start justify-between gap-2">
                <h3 className="font-semibold text-gray-900">{service.title}</h3>
                <button
                  onClick={() => handleCopy(service.title, 'title')}
                  className="flex-shrink-0 p-1 text-gray-400 hover:text-gray-600"
                >
                  {copiedField === 'title' ? <Check className="w-3.5 h-3.5 text-green-500" /> : <Copy className="w-3.5 h-3.5" />}
                </button>
              </div>
              {service.category && (
                <Badge className="mt-1 bg-primary-50 text-primary-700">{service.category}</Badge>
              )}
              <p className="text-sm text-gray-500 mt-1">{service.shortDesc}</p>
            </div>

            {websiteUrl && (
              <a
                href={websiteUrl}
                target="_blank"
                rel="noopener noreferrer"
                className="inline-flex items-center gap-1 text-xs text-primary-700 hover:text-primary-800"
              >
                <Link2 className="w-3.5 h-3.5" />
                Перейти на сайт
              </a>
            )}

            <div className="flex items-start justify-between gap-2">
              <p className="text-sm text-gray-700 leading-relaxed">{service.longDesc}</p>
              <button
                onClick={() => handleCopy(service.longDesc, 'longDesc')}
                className="flex-shrink-0 p-1 text-gray-400 hover:text-gray-600"
              >
                {copiedField === 'longDesc' ? <Check className="w-3.5 h-3.5 text-green-500" /> : <Copy className="w-3.5 h-3.5" />}
              </button>
            </div>

            {service.keywords.length > 0 && (
              <div className="flex flex-wrap items-center gap-1">
                <Tag className="w-3.5 h-3.5 text-gray-400" />
                {service.keywords.map((kw) => (
                  <Badge key={kw} tone="neutral">{kw}</Badge>
                ))}
              </div>
            )}

            <div className="flex gap-2 pt-3 border-t border-gray-100">
              <Button
                size="sm"
                variant="ghost"
                onClick={() => setIsEditing(true)}
                icon={<Edit2 className="w-3.5 h-3.5" />}
              >
                Редактировать
              </Button>
              {!imageUrl && (
                <Button
                  size="sm"
                  variant="ghost"
                  onClick={handleRegenerateImage}
                  loading={isRegeneratingImage}
                  icon={<Image className="w-3.5 h-3.5" />}
                  className="text-purple-600 hover:text-purple-900 hover:bg-purple-50"
                >
                  Создать фото
                </Button>
              )}
              <Button
                size="sm"
                variant="ghost"
                onClick={handleDelete}
                loading={isDeleting}
                icon={<Trash2 className="w-3.5 h-3.5" />}
                className="ml-auto text-red-500 hover:text-red-700 hover:bg-red-50"
              >
                Удалить
              </Button>
            </div>
          </>
        )}
      </div>
    </Card>
  );
}

export function Services() {
  const selectedBusinessId = useSelectedBusinessId() ?? '';
  const [businesses, setBusinesses] = useState<Business[]>([]);
  const [services, setServices] = useState<Service[]>([]);
  const [isLoading, setIsLoading] = useState(false);
  const [isGenerating, setIsGenerating] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [generateCount, setGenerateCount] = useState(5);
  const [progress, setProgress] = useState<string | null>(null);
  const [isCopiedYmlUrl, setIsCopiedYmlUrl] = useState(false);

  useEffect(() => {
    getBusinessList().then(setBusinesses).catch(() => {});
  }, []);

  useEffect(() => {
    if (selectedBusinessId) loadServices();
  }, [selectedBusinessId]);

  const loadServices = async () => {
    try {
      setIsLoading(true);
      const res = await servicesApi.getByBusiness(selectedBusinessId);
      setServices(res.data.data);
    } catch {
      setError('Не удалось загрузить услуги');
    } finally {
      setIsLoading(false);
    }
  };

  const handleGenerate = async () => {
    const selectedBusiness = businesses.find((b) => b.id === selectedBusinessId);
    if (!selectedBusiness?.targetAudience) {
      setError('Для лучшей генерации заполните бриф в настройках бизнеса (целевую аудиторию и УТП)');
    }

    setIsGenerating(true);
    setProgress('Генерируем тексты услуг через AI...');
    setError(null);

    try {
      setProgress('Генерируем услуги и изображения... Это может занять до 60 секунд');
      await servicesApi.generate(selectedBusinessId, generateCount);
      setProgress(null);
      await loadServices();
    } catch (err) {
      setError('Ошибка генерации услуг');
      setProgress(null);
    } finally {
      setIsGenerating(false);
    }
  };

  const selectedBusiness = businesses.find((b) => b.id === selectedBusinessId);
  const hasBrief = !!(selectedBusiness?.targetAudience || selectedBusiness?.tone);
  const yandexYmlUrl = selectedBusinessId ? servicesApi.getYandexYmlUrl(selectedBusinessId) : '';

  const handleCopyYml = async () => {
    if (!yandexYmlUrl) return;
    await navigator.clipboard.writeText(yandexYmlUrl);
    setIsCopiedYmlUrl(true);
    setTimeout(() => setIsCopiedYmlUrl(false), 2000);
  };

  return (
    <div>
      <PageHeader title="Услуги" subtitle="Генерация описаний услуг для Яндекс.Карт" />

      <div className="p-6 space-y-6">
        {error && (
          <div className="p-4 bg-red-50 border border-red-200 rounded-lg flex items-center gap-2 text-red-700">
            <AlertCircle className="w-5 h-5 flex-shrink-0" />
            <span>{error}</span>
            <button onClick={() => setError(null)} className="ml-auto"><X className="w-4 h-4" /></button>
          </div>
        )}

        {/* Generation panel */}
        <Card>
          <div className="flex flex-wrap items-end gap-4">
            {selectedBusiness && !hasBrief && (
              <div className="flex-1 min-w-[200px]">
                <p className="text-xs text-amber-600 mt-1">
                  Рекомендуем заполнить бриф в разделе "Бизнесы" для лучшей генерации
                </p>
              </div>
            )}

            <div className="w-32">
              <label className="block text-sm font-medium text-gray-700 mb-1">Количество</label>
              <select
                className={clsx(
                  'w-full px-3 py-2 border border-gray-300 rounded-lg text-gray-900',
                  'focus:outline-none focus:ring-2 focus:ring-primary-500 focus:border-primary-500'
                )}
                value={generateCount}
                onChange={(e) => setGenerateCount(Number(e.target.value))}
              >
                {[1,2,3,5,7,10].map((n) => (
                  <option key={n} value={n}>{n} услуг</option>
                ))}
              </select>
            </div>

            <Button
              icon={isGenerating ? <Loader2 className="w-4 h-4 animate-spin" /> : <Sparkles className="w-4 h-4" />}
              onClick={handleGenerate}
              loading={isGenerating}
            >
              Сгенерировать под ключ
            </Button>
          </div>

          {progress && (
            <div className="mt-4 flex items-center gap-3 p-3 bg-purple-50 border border-purple-200 rounded-lg">
              <Loader2 className="w-5 h-5 animate-spin text-purple-600 flex-shrink-0" />
              <p className="text-sm text-purple-700">{progress}</p>
            </div>
          )}

          {selectedBusinessId && (
            <div className="mt-6 p-4 rounded-xl border border-primary-200 bg-primary-50">
              <p className="text-sm text-primary-900 font-medium">YML-фид для Яндекс Бизнес</p>
              <p className="text-xs text-primary-700 mt-1">
                Подключите эту ссылку один раз в Яндекс Бизнес: О компании → Товары и услуги → Источник → YML-фид.
              </p>
              <div className="mt-3 flex flex-wrap gap-2 items-center">
                <div className="flex-1 min-w-[280px]">
                  <Input
                    className="text-xs bg-white border-primary-200"
                    value={yandexYmlUrl}
                    readOnly
                  />
                </div>
                <Button size="sm" variant="outline" onClick={handleCopyYml}>
                  {isCopiedYmlUrl ? 'Скопировано' : 'Копировать'}
                </Button>
                <a
                  href={yandexYmlUrl}
                  target="_blank"
                  rel="noopener noreferrer"
                  className="inline-flex items-center gap-1 px-3 py-2 text-xs text-primary-700 hover:text-primary-900 border border-primary-200 rounded-lg bg-white"
                >
                  <Link2 className="w-3.5 h-3.5" />
                  Открыть
                </a>
              </div>
            </div>
          )}
        </Card>

        {/* Services grid */}
        {isLoading ? (
          <div className="flex justify-center py-12">
            <Loader2 className="w-8 h-8 animate-spin text-primary-600" />
          </div>
        ) : services.length === 0 ? (
          <div className="text-center py-16 text-gray-500">
            <Sparkles className="w-12 h-12 mx-auto mb-3 text-gray-300" />
            <p className="font-medium text-lg">Нет услуг</p>
            <p className="text-sm mt-1">Нажмите "Сгенерировать под ключ" чтобы создать описания и изображения услуг</p>
          </div>
        ) : (
          <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-6">
            {services.map((service) => (
              <ServiceCard key={service.id} service={service} onReload={loadServices} />
            ))}
          </div>
        )}
      </div>
    </div>
  );
}
